import moment from 'moment';
import { TFunction } from 'i18next';
import { DATE_FORMAT_DEFAUT } from 'Config';
import Utils from 'common/services/Utils';
import { ChargeValidationGroup, ChargeValidationRow, ChargeValidationWarning, ChargeValidationWarningDetail } from './models';

// Task #66220: lógica partilhada pelos avisos de validação de despesas — usada pelo modal de
// justificação (importação e despesa manual) e pelos cartões de detalhe. Antes estava duplicada
// entre o ImportChargeScreen e o ChargeScreen.

/** Passa a primeira letra de cada chave para minúscula, recursivamente. */
const toCamelCase = (value: any): any => {
    if (Array.isArray(value)) return value.map(toCamelCase);
    if (value === null || typeof value !== 'object') return value;
    return Object.keys(value).reduce<Record<string, any>>((acc, key) => {
        acc[key.charAt(0).toLowerCase() + key.slice(1)] = toCamelCase(value[key]);
        return acc;
    }, {});
};

/**
 * Avisos gravados como array JSON. Tolera camelCase (atual) e PascalCase (importações antigas,
 * gravadas com o JsonConvert cru) para o texto aparecer sempre.
 */
export const parseWarnings = (raw?: string): ChargeValidationWarning[] => {
    if (!raw) return [];
    try {
        const parsed = toCamelCase(JSON.parse(raw));
        if (!Array.isArray(parsed)) return [];
        return parsed.map((w: any) => ({
            code: w.code ?? '',
            field: w.field ?? '',
            message: w.message ?? '',
            data: w.data,
        }));
    } catch {
        return [];
    }
};

/** Junta as mensagens de uma linha numa só frase — usado nos tooltips, onde só cabe texto. */
export const formatWarningMessages = (msgs: (string | undefined)[]): string => {
    const clean = msgs.map(m => (m ?? '').trim().replace(/\.\s*$/, '')).filter(m => m.length > 0);
    return clean.length ? clean.join('; ') + '.' : '';
};

const num = (value?: number | null): string =>
    value === null || value === undefined ? '' : Utils.numberFormatter(value);

const date = (value?: string | Date | null): string =>
    value ? moment(value).format(DATE_FORMAT_DEFAUT) : '';

/** "1", "1 e 2", "1, 2 e 3" */
export const formatLineList = (lines: number[], t: TFunction): string => {
    if (lines.length <= 1) return `${lines[0] ?? ''}`;
    return `${lines.slice(0, -1).join(', ')} ${t('common.and')} ${lines[lines.length - 1]}`;
};

/**
 * Traduz um aviso no bloco de detalhe da maquete: título, linhas "label: valor" e nota de ajuda.
 * Devolve null para os avisos gravados antes do #66220 (sem `data`), que continuam a mostrar a
 * frase original do backend.
 */
export const describeWarning = (warning: ChargeValidationWarning, t: TFunction): ChargeValidationWarningDetail | null => {
    const d = warning.data;
    if (!d) return null;

    const key = `${warning.code}|${warning.message}`;

    switch (warning.code) {
        case 'KMS_DECREASE': {
            if (d.kms === undefined || d.otherKms === undefined) return null;
            const isNext = d.direction === 'NEXT';
            const diff = Math.abs(d.otherKms - d.kms);
            return {
                key,
                title: t('charges.validation.details.kms_decrease_title') as string,
                rows: [
                    {
                        label: t('charges.validation.details.this_record') as string,
                        value: `${date(d.date)} — ${num(d.kms)} km`,
                    },
                    {
                        label: t(isNext ? 'charges.validation.details.later_record' : 'charges.validation.details.earlier_record') as string,
                        value: `${date(d.otherDate)} — ${num(d.otherKms)} km`,
                    },
                ],
                hint: t(isNext ? 'charges.validation.details.kms_decrease_hint_next' : 'charges.validation.details.kms_decrease_hint_prev', { diff: num(diff) }) as string,
            };
        }

        case 'DAILY_KM_EXCEEDED': {
            return {
                key,
                title: t('charges.validation.details.daily_km_title') as string,
                rows: [
                    {
                        label: t('charges.validation.details.daily_km_average') as string,
                        value: `${num(d.kmPerDay)} ${t('charges.validation.details.per_day')}`,
                    },
                    {
                        label: t('charges.validation.details.daily_km_limit') as string,
                        value: `${num(d.limit)} ${t('charges.validation.details.per_day')}`,
                    },
                ],
                hint: t('charges.validation.details.daily_km_hint', { from: date(d.otherDate), to: date(d.date), days: d.days ?? 0 }) as string,
            };
        }

        case 'TANK_EXCEEDED':
        case 'KWH_EXCEEDED': {
            const isEnergy = warning.code === 'KWH_EXCEEDED';
            const unit = d.unit ?? (isEnergy ? 'kWh' : 'L');
            return {
                key,
                title: t('charges.validation.details.capacity_title') as string,
                rows: [
                    {
                        label: t(isEnergy ? 'charges.validation.details.charged' : 'charges.validation.details.refuelled') as string,
                        value: `${num(d.qty)} ${unit}`,
                    },
                    {
                        label: t(isEnergy ? 'charges.validation.details.battery_capacity' : 'charges.validation.details.tank_capacity') as string,
                        value: `${num(d.capacity)} ${unit}`,
                    },
                ],
            };
        }

        case 'DUPLICATE': {
            const lines = d.lines ?? [];
            const values = [
                d.kms !== undefined && d.kms !== null ? `${num(d.kms)} km` : null,
                d.qty !== undefined && d.qty !== null ? `${num(d.qty)} ${d.unit ?? 'L'}` : null,
                d.cost !== undefined && d.cost !== null ? `${num(d.cost)} €` : null,
            ].filter(v => !!v).join(' · ');

            if (lines.length > 1) {
                return {
                    key,
                    title: t('charges.validation.details.duplicate_row_title') as string,
                    rows: [
                        {
                            label: t(lines.length === 2 ? 'charges.validation.details.duplicate_both_lines' : 'charges.validation.details.duplicate_all_lines') as string,
                            value: values,
                        },
                    ],
                    hint: d.existingCharge ? t('charges.validation.details.duplicate_existing_hint') as string : undefined,
                };
            }

            return {
                key,
                title: t('charges.validation.details.duplicate_existing_title') as string,
                rows: [
                    {
                        label: t('charges.validation.details.duplicate_existing_label') as string,
                        value: [date(d.date), values].filter(v => !!v).join(' · '),
                    },
                ],
            };
        }

        default:
            return null;
    }
};

/**
 * Agrupa as linhas que se justificam em bloco: as que duplicam entre si (o aviso DUPLICATE traz as
 * linhas irmãs) ficam num só grupo, as restantes ficam sozinhas.
 */
export const groupValidationRows = (rows: ChargeValidationRow[]): ChargeValidationGroup[] => {
    const byLine = new Map<number, ChargeValidationRow>();
    rows.forEach(r => byLine.set(r.line, r));

    // Union-find simples sobre as linhas duplicadas.
    const parent = new Map<number, number>();
    const find = (line: number): number => {
        const p = parent.get(line);
        if (p === undefined || p === line) return line;
        const root = find(p);
        parent.set(line, root);
        return root;
    };
    const union = (a: number, b: number) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb) parent.set(Math.max(ra, rb), Math.min(ra, rb));
    };

    rows.forEach(r => {
        parent.set(r.line, parent.get(r.line) ?? r.line);
        r.warnings.forEach(w => {
            (w.data?.lines ?? []).forEach(other => {
                if (byLine.has(other)) union(r.line, other);
            });
        });
    });

    const groups = new Map<number, ChargeValidationGroup>();
    rows.forEach(r => {
        const root = find(r.line);
        const group = groups.get(root);
        if (group) {
            group.lines.push(r.line);
            group.rows.push(r);
        } else {
            groups.set(root, { key: `g${root}`, lines: [r.line], rows: [r] });
        }
    });

    return Array.from(groups.values()).map(g => ({
        ...g,
        lines: g.lines.sort((a, b) => a - b),
        rows: g.rows.sort((a, b) => a.line - b.line),
    }));
};
